/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.application


import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import kotlinx.dom.createElement
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.backend.webgl.WebGLBackend
import uk.co.nickthecoder.glok.util.*
import kotlin.reflect.KClass
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLDivElement
import kotlin.js.Date

/**
 * Starts the Glok application.
 * @param containerName The id of an HTML element (usually a DIV) where the glok canvas will be placed.
 */
fun launchInContainer(
    klass: KClass<out Application>,
    containerName: String,
    rawArgs: Array<out String> = emptyArray()
) {
    console.log("Document Loaded")
    val container = window.document.getElementById(containerName) as HTMLDivElement?

    if (container == null) {
        console.log("Error. div with id='${containerName}' not found")
    } else {
        // Glok does NOT create a `canvas`, we need to do that ourselves.
        // This gives us full control over the size of the canvas, and also
        // lets us include other HTML elements.
        // For simplicity, this demo uses a fixed size canvas with no other HTML elements.
        val glokCanvas = window.document.createElement("canvas") {
            id = "glokCanvas"
        } as HTMLCanvasElement
        with(glokCanvas) {
            width = container.clientWidth
            height = container.clientHeight
            onmousemove = { e -> WebGLBackend.processMouseMove(e) }
            onmousedown = { e -> WebGLBackend.processMouseDown(e) }
            onmouseup = { e -> WebGLBackend.processMouseUp(e) }
            oncontextmenu = {e -> WebGLBackend.processContextMenu(e) }
            onwheel = {e -> WebGLBackend.processWheel(e) }
        }
        with(window) {
            onkeydown = { e -> WebGLBackend.processKeyDown(e) }
            onkeyup = { e -> WebGLBackend.processKeyUp(e) }
            onresize = { WebGLBackend.processResize() }
        }

        val fontCanvas = window.document.createElement("canvas") {
            id = "fontCanvas"
        } as HTMLCanvasElement
        fontCanvas.width = 1
        fontCanvas.height = 1
        fontCanvas.hidden = true

        while (container.firstChild != null) {
            container.removeChild(container.lastChild !!)
        }
        container.appendChild(glokCanvas)
        container.appendChild(fontCanvas)

        launch(klass, rawArgs)
    }
}

actual fun launch(klass: KClass<out Application>, rawArgs: Array<out String>) {
    if (Application.running) throw GlokException("Glok has already started")

    log = ConsoleLog().apply { level = Log.TRACE }
    log.info("Launching Glok Application")

    Application.running = true

    //log.info("Creating WebGLBackend")
    backend = WebGLBackend()
    //log.info("backend created")
    Platform.begin()

    //log.info("Glok launchPart2")
    Application.launchPart2(Restart(klass, rawArgs))
    //log.info("Glok launchPart2 complete")
}

@OptIn(DelicateCoroutinesApi::class)
actual fun loop(application: Application) {
    // Ensures the scene grows to the size of the glok canvas.
    // Unlike applications running in the JVM, the application has no control over the size
    // of the stage or scene - they always fill the canvas.
    WebGLBackend.processResize()

    GlobalScope.launch {

        while (application.stages.isNotEmpty()) {
            val preLoopMillis = Date.now()
            Application.loopBody(application)

            val elapsedMillis = (Date.now() - preLoopMillis)
            val delayDurationMillis = max(1.0, Application.idleFramePeriod - elapsedMillis)
            delay(delayDurationMillis.toLong())
        }
        log.info("loop finished : Stage count : ${application.stages.size}")
    }
}
